import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, notification, Modal, Button, Spin } from "antd";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useRouteMetrics } from "./hooks/useRouteMetrics";
import { useMsal } from "@azure/msal-react";
import { withAuth } from "./providers/with-auth";
import { loginRequest } from "./providers/config";
import tokenManager from "./core/TokenManager";
import { formatClinicianName } from "./utils/formatClinicianName"; // replaces the local formatName that was defined here
import Dashboard from "./pages/Dashboard";
import SessionDetails from "./pages/Session";
import LoadingSkeleton from "./providers/loader.jsx";
import { getClinicianByToken } from "./services/api";
import { /* handleLogin, */ handleLogout } from "./providers/with-auth";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FAQ from "./pages/Faq";
import ManageClinicians from "./pages/ManageClinicians";
import TemplateBuilder from "./pages/TemplateBuilder";
import TemplateLibrary from "./pages/TemplateLibrary";
import PracticeDefaults from "./pages/PracticeDefaults";
import Profile from "./pages/Profile";

// ---------------------------------------------------------------------------
// Auth recovery modal states
// ---------------------------------------------------------------------------
/** No auth issue — normal operation. */
const AUTH_STATE_IDLE = 'idle';
/** Tier 2: Showing popup re-auth modal — waiting for user to click. */
const AUTH_STATE_POPUP_PROMPT = 'popup_prompt';
/** Tier 2: Popup is open — waiting for Azure AD response. */
const AUTH_STATE_POPUP_IN_PROGRESS = 'popup_in_progress';
/** Tier 3: All recovery failed — showing countdown to forced logout. */
const AUTH_STATE_FORCE_LOGOUT = 'force_logout';

/** Countdown seconds before auto-logout in Tier 3. */
const LOGOUT_COUNTDOWN_SECONDS = 60;

// Custom hook for conditionally using MSAL
const useMsalIfNeeded = () => {
  const isAuthBypassed = process.env.REACT_APP_BYPASS_AUTH === 'true';

  if (isAuthBypassed) {
    return { instance: null, accounts: [] };
  }

  try {
    return useMsal();
  } catch (error) {
    console.log("MSAL not available, using mock data");
    return { instance: null, accounts: [] };
  }
};

const ClinicianNotFoundCard = ({ instance }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-500 text-2xl font-bold">!</span>
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Clinician Not Found
        </h2>

        <p className="text-gray-600 mb-6">
          Please login with a valid clinician account to continue.
        </p>

        <button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={() => {
            // Add your login redirect logic here
            handleLogout(instance);
            console.log("Redirecting to login...");
          }}
        >
          Go to Login
        </button>
      </div>
    </div>
  );
};

function AppContent() {
  const isAuthBypassed = process.env.REACT_APP_BYPASS_AUTH === 'true';
  const { instance, accounts } = useMsalIfNeeded();

  const [selectedDoctorId, setSelectedDoctorId] = useState();
  const [selectedDate, setSelectedDate] = useState("");
  const [tokenManagerReady, setTokenManagerReady] = useState(isAuthBypassed);
  const [gotIds, setGotIds] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [invalidUser, setInvalidUser] = useState(false);

  // -----------------------------------------------------------------------
  // Auth recovery state machine
  // -----------------------------------------------------------------------
  const [authState, setAuthState] = useState(AUTH_STATE_IDLE);
  const [logoutCountdown, setLogoutCountdown] = useState(LOGOUT_COUNTDOWN_SECONDS);
  const countdownRef = useRef(null);

  const isAdmin = useMemo(() => {
    return options?.roles?.includes?.("admin");
  }, [options]);

  useEffect(() => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const year = today.getFullYear();
    setSelectedDate(`${month}/${day}/${year}`);
  }, []);

  // -----------------------------------------------------------------------
  // Initialize TokenManager once MSAL account is available
  // -----------------------------------------------------------------------
  // TokenManager handles ALL token acquisition and proactive refresh.
  // The Axios interceptors in index-core.js use TokenManager.getToken()
  // for every request, so no separate interceptor setup is needed here.
  useEffect(() => {
    if (isAuthBypassed) {
      console.log("[App] Auth bypassed — skipping TokenManager initialization");
      return;
    }

    if (accounts[0] && !tokenManagerReady) {
      console.log("[App] Initializing TokenManager with account:", accounts[0]?.username || accounts[0]?.name);
      tokenManager.initialize(instance, accounts[0]);
      setTokenManagerReady(true);
    }
  }, [instance, accounts, tokenManagerReady, isAuthBypassed]);

  // -----------------------------------------------------------------------
  // Tier 2: Handle `token:relogin-required` — show popup re-auth modal
  // -----------------------------------------------------------------------
  // TokenManager emits this when ssoSilent (Tier 1) fails and it needs
  // the UI to attempt acquireTokenPopup().
  useEffect(() => {
    if (isAuthBypassed) return;

    const handleReloginRequired = (event) => {
      console.warn("[App] Tier 2: token:relogin-required received", event.detail);
      setAuthState(AUTH_STATE_POPUP_PROMPT);
    };

    window.addEventListener('token:relogin-required', handleReloginRequired);
    return () => window.removeEventListener('token:relogin-required', handleReloginRequired);
  }, [isAuthBypassed]);

  // -----------------------------------------------------------------------
  // Tier 3: Handle `token:auth-failure` — show forced logout modal
  // -----------------------------------------------------------------------
  // This fires when ALL recovery tiers (ssoSilent + popup) have failed,
  // OR when the Axios interceptor's 401 retry is exhausted.
  useEffect(() => {
    if (isAuthBypassed) return;

    const handleAuthFailure = (event) => {
      console.error("[App] Tier 3: Unrecoverable auth failure:", event.detail);
      // Only escalate to force-logout if we're not already in that state
      if (authState !== AUTH_STATE_FORCE_LOGOUT) {
        setAuthState(AUTH_STATE_FORCE_LOGOUT);
        setLogoutCountdown(LOGOUT_COUNTDOWN_SECONDS);
      }
    };

    window.addEventListener('token:auth-failure', handleAuthFailure);
    return () => window.removeEventListener('token:auth-failure', handleAuthFailure);
  }, [isAuthBypassed, authState]);

  // -----------------------------------------------------------------------
  // Tier 3: Countdown timer for auto-logout
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (authState !== AUTH_STATE_FORCE_LOGOUT) {
      // Clear any running countdown when leaving force-logout state
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    // Start countdown
    countdownRef.current = setInterval(() => {
      setLogoutCountdown((prev) => {
        if (prev <= 1) {
          // Time's up — force logout
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          console.log("[App] Auto-logout countdown reached 0 — forcing logout");
          if (instance) {
            handleLogout(instance);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [authState, instance]);

  // -----------------------------------------------------------------------
  // Tier 2: Attempt popup re-authentication
  // -----------------------------------------------------------------------
  const attemptPopupReauth = useCallback(async () => {
    if (!instance) {
      console.error("[App] No MSAL instance — cannot attempt popup");
      tokenManager.resolvePopupRecovery(null);
      setAuthState(AUTH_STATE_FORCE_LOGOUT);
      setLogoutCountdown(LOGOUT_COUNTDOWN_SECONDS);
      return;
    }

    setAuthState(AUTH_STATE_POPUP_IN_PROGRESS);

    try {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
      const request = {
        ...loginRequest,
        account,
      };

      console.log("[App] Tier 2: Opening acquireTokenPopup...");
      const response = await instance.acquireTokenPopup(request);

      if (response?.idToken) {
        console.log("[App] Tier 2 SUCCESS: Popup returned a valid token");

        // Notify TokenManager that popup succeeded
        tokenManager.resolvePopupRecovery({
          idToken: response.idToken,
          account: response.account,
        });

        // Also update the active account
        if (response.account) {
          instance.setActiveAccount(response.account);
        }

        // Reset auth state — back to normal
        setAuthState(AUTH_STATE_IDLE);

        // Dismiss any lingering notifications
        notification.destroy('token-refresh-warning');
      } else {
        console.warn("[App] Tier 2: Popup returned no idToken");
        tokenManager.resolvePopupRecovery(null);
        // Escalate to Tier 3
        setAuthState(AUTH_STATE_FORCE_LOGOUT);
        setLogoutCountdown(LOGOUT_COUNTDOWN_SECONDS);
      }
    } catch (err) {
      console.error("[App] Tier 2: Popup re-auth failed:", err?.message || err);
      tokenManager.resolvePopupRecovery(null);
      // Escalate to Tier 3
      setAuthState(AUTH_STATE_FORCE_LOGOUT);
      setLogoutCountdown(LOGOUT_COUNTDOWN_SECONDS);
    }
  }, [instance]);

  // -----------------------------------------------------------------------
  // Listen for token refresh failures (non-fatal — token may still be valid)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isAuthBypassed) return;

    const handleRefreshFailed = (event) => {
      const { cachedTokenStillValid, tokenExpiresIn } = event.detail || {};
      if (!cachedTokenStillValid) {
        // Token is truly expired and refresh failed — show warning
        notification.warning({
          key: 'token-refresh-warning',
          message: "Session Refresh Issue",
          description: "Unable to refresh your session. Please save your work. You may need to log in again shortly.",
          duration: 0, // Don't auto-dismiss
        });
      } else {
        // Token still valid but refresh failed — log only, will retry
        console.warn("[App] Token refresh failed but cached token still valid. Expires in:", tokenExpiresIn);
      }
    };

    window.addEventListener('token:refresh-failed', handleRefreshFailed);
    return () => window.removeEventListener('token:refresh-failed', handleRefreshFailed);
  }, [isAuthBypassed]);

  // -----------------------------------------------------------------------
  // Listen for successful token recovery — dismiss any auth modals
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isAuthBypassed) return;

    const handleTokenRefreshed = (event) => {
      const { recoveryTier } = event.detail || {};
      if (recoveryTier) {
        console.log("[App] Token recovered via tier:", recoveryTier);
        // If we were showing an auth modal, dismiss it
        if (authState !== AUTH_STATE_IDLE) {
          setAuthState(AUTH_STATE_IDLE);
          notification.destroy('token-refresh-warning');
          notification.success({
            key: 'token-recovered',
            message: "Session Restored",
            description: "Your session has been successfully refreshed.",
            duration: 5,
          });
        }
      }
    };

    window.addEventListener('token:refreshed', handleTokenRefreshed);
    return () => window.removeEventListener('token:refreshed', handleTokenRefreshed);
  }, [isAuthBypassed, authState]);

  // -----------------------------------------------------------------------
  // Update TokenManager account when MSAL events change the active account
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isAuthBypassed) return;

    if (accounts[0] && tokenManagerReady) {
      tokenManager.setAccount(accounts[0]);
    }
  }, [accounts, tokenManagerReady, isAuthBypassed]);

  // -----------------------------------------------------------------------
  // Fetch clinician details
  // -----------------------------------------------------------------------
  useEffect(() => {
    let ok = true;
    async function getIds() {
      try {
        setLoading(true);

        if (isAuthBypassed) {
          try {
            const opts = await getClinicianByToken();
            setSelectedDoctorId(opts?.clinician_id);
            if (ok) setOptions(opts);
          } catch (apiError) {
            console.log("API call failed in bypassed mode, using hardcoded data:", apiError);
            const opts = {
              "clinician_id": 3,
              "first_name": "JOHNATHAN",
              "middle_name": "",
              "last_name": "JONES",
              "profile_picture_url": null,
              "email": null,
              "clinician_type": "prescriber"
            };
            setSelectedDoctorId(opts.clinician_id);
            if (ok) setOptions(opts);
          }
        } else {
          const opts = await getClinicianByToken();
          //   const opts={
          //     "clinician_id": 5,
          //     "first_name": "MD",
          //     "middle_name": "",
          //     "last_name": "MICHAEL D RICHEY",
          //     "profile_picture_url": null,
          //     "email": null,
          //     "clinician_type": "therapist"
          // }
          setSelectedDoctorId(opts?.clinician_id);
          if (ok) setOptions(opts);
        }
      } catch (err) {
        console.log("invalid user", err);
        setInvalidUser(true);
      } finally {
        if (ok) setLoading(false);
        setGotIds(true);
      }
    }

    // Wait for TokenManager to be ready before making API calls
    if (!gotIds && tokenManagerReady) getIds();
    return () => {
      ok = false;
    };
  }, [tokenManagerReady, gotIds, isAuthBypassed]);

  // -----------------------------------------------------------------------
  // Cleanup TokenManager on unmount
  // -----------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (!isAuthBypassed) {
        tokenManager.destroy();
      }
    };
  }, [isAuthBypassed]);

  if (!tokenManagerReady || loading) return <LoadingSkeleton />;

  if (invalidUser) return <ClinicianNotFoundCard instance={instance} />;

  return (
    <ConfigProvider>
      {/* ----------------------------------------------------------------- */}
      {/* Tier 2: Popup Re-authentication Modal                             */}
      {/* Shown when ssoSilent (Tier 1) failed and we need user interaction */}
      {/* to get a fresh token via a popup — page stays intact.             */}
      {/* ----------------------------------------------------------------- */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🔒</span>
            <span>Session Expired</span>
          </div>
        }
        open={authState === AUTH_STATE_POPUP_PROMPT || authState === AUTH_STATE_POPUP_IN_PROGRESS}
        closable={false}
        maskClosable={false}
        footer={
          authState === AUTH_STATE_POPUP_IN_PROGRESS
            ? [
                <Button key="waiting" disabled>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  Authenticating...
                </Button>,
              ]
            : [
                <Button
                  key="reauth"
                  type="primary"
                  onClick={attemptPopupReauth}
                >
                  Re-authenticate
                </Button>,
                <Button
                  key="logout"
                  onClick={() => {
                    tokenManager.resolvePopupRecovery(null);
                    handleLogout(instance);
                  }}
                >
                  Logout Instead
                </Button>,
              ]
        }
      >
        <p style={{ marginBottom: 8 }}>
          Your session has expired and could not be refreshed automatically.
        </p>
        <p style={{ marginBottom: 8 }}>
          Click <strong>Re-authenticate</strong> to sign in via a popup window.
          Your current page and any unsaved work will be preserved.
        </p>
        {authState === AUTH_STATE_POPUP_IN_PROGRESS && (
          <p style={{ color: '#1677ff', marginTop: 12 }}>
            A sign-in popup has opened. Please complete the authentication in the popup window.
          </p>
        )}
      </Modal>

      {/* ----------------------------------------------------------------- */}
      {/* Tier 3: Forced Logout Modal with Countdown                        */}
      {/* Shown when ALL recovery tiers have failed — user must re-login.   */}
      {/* Auto-logs out after LOGOUT_COUNTDOWN_SECONDS.                     */}
      {/* ----------------------------------------------------------------- */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <span>Authentication Required</span>
          </div>
        }
        open={authState === AUTH_STATE_FORCE_LOGOUT}
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="logout"
            type="primary"
            danger
            onClick={() => {
              if (instance) handleLogout(instance);
            }}
          >
            Log Out Now
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 8 }}>
          Your session could not be restored. All automatic recovery attempts have been exhausted.
        </p>
        <p style={{ marginBottom: 8 }}>
          <strong>Please save any important work</strong> before logging out.
          You will need to sign in again to continue.
        </p>
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6,
            textAlign: 'center',
          }}
        >
          <p style={{ margin: 0, color: '#cf1322', fontWeight: 500 }}>
            Auto-logout in{' '}
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {logoutCountdown}
            </span>{' '}
            second{logoutCountdown !== 1 ? 's' : ''}
          </p>
        </div>
      </Modal>

      <BrowserRouter>
        <RouteMetricsTracker />
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                selectedDoctorId={selectedDoctorId}
                setSelectedDoctorId={setSelectedDoctorId}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                options={options}
              />
            }
          />
          <Route
            path="patient/:patient_id/session/:id"
            element={
              <SessionDetails
                selectedDoctorId={selectedDoctorId}
                setSelectedDoctorId={setSelectedDoctorId}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                options={options}
              />
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <PrivacyPolicy
                selectedDoctorId={selectedDoctorId}
                setSelectedDoctorId={setSelectedDoctorId}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                options={options}
              />
            }
          />
          <Route
            path="/faq"
            element={
              <FAQ
                selectedDoctorId={selectedDoctorId}
                setSelectedDoctorId={setSelectedDoctorId}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                options={options}
              />
            }
          />
          <Route
            path="/profile"
            element={
              <Profile
                options={options}
                onSaved={(updated) => setOptions((prev) => ({ ...prev, ...updated }))}
              />
            }
          />
          {isAdmin && (
            <>
              <Route
                path="/admin/clinician"
                element={
                  <ManageClinicians
                    selectedDoctorId={selectedDoctorId}
                    setSelectedDoctorId={setSelectedDoctorId}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    options={options}
                  />
                }
              />
              <Route path="/admin/templates" element={<TemplateLibrary />} />
              <Route path="/admin/template-builder" element={<TemplateBuilder />} />
              <Route path="/admin/template-builder/:templateId" element={<TemplateBuilder />} />
              <Route path="/admin/practice-defaults" element={<PracticeDefaults />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

/**
 * Invisible component that lives inside <BrowserRouter> to track
 * SPA route transitions as clickstream `navigation` events.
 */
function RouteMetricsTracker() {
  useRouteMetrics();
  return null;
}

function App() {
  return <AppContent />;
}

export default withAuth(App);
// export default App;
